/**
 * Upsell emballage cadeau au passage au paiement.
 *
 * Intercepte le clic sur « Passer au paiement » (panier page + drawer) et
 * propose selon le contenu du panier (hors lignes du compositeur _coffretId) :
 * - paires seules (SKU 1-CH-…)        → offre « coffret » (coffret une paire)
 * - packs de 3 (SKU 3-CH-…)           → offre « pack » (Pack 3 Paires)
 * - 3 paires seules ou plus           → LES DEUX (au choix du client)
 * Rien si un emballage (1-COF-…) ou un coffret composé (_coffretId) est déjà
 * au panier. « Passer au paiement » (popup) → checkout direct, sans re-proposer
 * pendant 30 minutes. Ajout → POST /cart/add puis checkout.
 */
const DISMISS_KEY = 'coffretUpsellDismissed';
const DISMISS_TTL_MS = 30 * 60 * 1000;

const isDismissed = () => {
  const at = Number(sessionStorage.getItem(DISMISS_KEY) || 0);
  return at > 0 && Date.now() - at < DISMISS_TTL_MS;
};


class CartCoffretUpsell extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-ref="coffret-upsell-modal"]');
    if (!this.modal) return;

    // Une entrée d'état par offre rendue (coffret et/ou pack)
    this.offers = [...this.querySelectorAll('[data-ref="upsell-offer"]')].map((el) => {
      const offer = {
        el,
        kind: el.dataset.offerKind,
        variantId: Number(el.dataset.variantId),
        unitPrice: Number(el.dataset.unitPrice),
        label: el.dataset.label || 'le coffret',
        labelPlural: el.dataset.labelPlural || 'coffrets',
        qty: 1,
        valueEl: el.querySelector('[data-ref="upsell-qty-value"]'),
        addButton: el.querySelector('[data-ref="upsell-add"]'),
      };
      el.querySelector('[data-ref="upsell-qty-minus"]')?.addEventListener('click', () => this.setQty(offer, offer.qty - 1));
      el.querySelector('[data-ref="upsell-qty-plus"]')?.addEventListener('click', () => this.setQty(offer, offer.qty + 1));
      offer.addButton?.addEventListener('click', () => this.addOffer(offer));
      return offer;
    });
    if (!this.offers.length) return;

    const scope = this.closest('.shopify-section') || document;
    this.checkoutButton = scope.querySelector('[data-ref="checkout-button"]');
    if (!this.checkoutButton) return;

    this.onCheckoutClick = this.onCheckoutClick.bind(this);
    this.checkoutButton.addEventListener('click', this.onCheckoutClick);

    this.querySelector('[data-ref="upsell-close"]')?.addEventListener('click', () => this.modal.close());
    this.querySelector('[data-ref="upsell-skip"]')?.addEventListener('click', () => {
      sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
      this.modal.close();
      this.goToCheckout();
    });
  }

  disconnectedCallback() {
    this.checkoutButton?.removeEventListener('click', this.onCheckoutClick);
  }

  async onCheckoutClick(event) {
    if (isDismissed()) return;

    // On bloque la soumission tout de suite : la décision (montrer ou non la
    // popup) est asynchrone. Si rien à montrer, on relance le checkout.
    event.preventDefault();
    event.stopPropagation();

    let visibleOffers = 0;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const items = cart.items || [];
      const hasPackaging = items.some(
        (item) =>
          (item.sku || '').startsWith('1-COF')
          || (item.properties && item.properties._coffretId)
      );
      // Seules les lignes hors compositeur comptent (les paires d'un pack
      // composé portent _coffretId — exclues du comptage)
      const looseItems = items.filter((item) => !(item.properties && item.properties._coffretId));
      const loosePairCount = looseItems
        .filter((item) => (item.sku || '').startsWith('1-CH-'))
        .reduce((sum, item) => sum + (item.quantity || 0), 0);
      const hasPackOf3 = looseItems.some((item) => (item.sku || '').startsWith('3-CH-'));

      const triggers = {
        coffret: loosePairCount >= 1,
        // Pack 3 paires : pour les packs 3-CH, et aussi dès 3 paires seules
        // (le client choisit alors coffret OU pack)
        pack: hasPackOf3 || loosePairCount >= 3,
      };
      for (const offer of this.offers) {
        const triggered = !hasPackaging && Boolean(triggers[offer.kind]);
        offer.el.hidden = !triggered;
        if (triggered) {
          this.setQty(offer, 1);
          visibleOffers += 1;
        }
      }
      this.modal.classList.toggle('coffret-upsell--multi', visibleOffers > 1);
    } catch {
      /* panier illisible : checkout direct */
    }

    if (visibleOffers > 0) {
      try {
        this.modal.showModal();
        return;
      } catch {
        /* dialog indisponible : checkout direct */
      }
    }
    this.goToCheckout();
  }

  async addOffer(offer) {
    if (!offer.variantId) return this.goToCheckout();
    if (offer.addButton) offer.addButton.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: offer.variantId, quantity: offer.qty }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.goToCheckout();
    } catch {
      // L'ajout a échoué (rupture…) : on n'enferme pas le client
      if (offer.addButton) offer.addButton.disabled = false;
      this.modal.close();
      this.goToCheckout();
    }
  }

  setQty(offer, next) {
    offer.qty = Math.min(Math.max(1, next), 99);
    if (offer.valueEl) offer.valueEl.textContent = String(offer.qty);
    if (offer.addButton && offer.unitPrice) {
      const total = ((offer.unitPrice * offer.qty) / 100).toFixed(2).replace('.', ',');
      offer.addButton.textContent = offer.qty > 1
        ? `Ajouter ${offer.qty} ${offer.labelPlural} · ${total} €`
        : `Ajouter ${offer.label} · ${total} €`;
    }
  }

  goToCheckout() {
    window.location.href = '/checkout';
  }
}

if (!customElements.get('cart-coffret-upsell')) {
  customElements.define('cart-coffret-upsell', CartCoffretUpsell);
}
